export const contentEn = {
  lang: 'en',
  meta: {
    home: {
      title: 'Doña Blanca — Industrial Cafeterias & Enterprise Foodservice Solutions',
      description: 'Turnkey corporate dining and industrial foodservice solutions across Mexico City, backed by over a decade of operational excellence and our 13 traditional restaurant locations.',
    },
    business: {
      title: 'Enterprise Foodservice & Corporate Cafeterias | Doña Blanca',
      description: 'Tailored foodservice models for businesses: on-site industrial kitchens, executive dining, daily meal delivery and multi-site distribution engineered for scale and consistent flavor.',
    },
    locations: {
      title: 'Our Restaurant Locations in Mexico City | Doña Blanca',
      description: 'Discover our 13 neighborhood locations across Mexico City: authentic everyday Mexican cuisine, hearty breakfasts, daily lunch specials and freshly baked bread.',
    },
    invoice: {
      title: 'Contact & Electronic Invoice (CFDI) | Doña Blanca',
      description: 'Request your electronic invoice for restaurant receipts or contact our enterprise team for customized corporate dining quotes and business food programs.',
    },
    title: 'Doña Blanca — Industrial Cafeterias & Enterprise Foodservice Solutions',
    description: 'Turnkey corporate dining and industrial foodservice solutions across Mexico City, backed by over a decade of operational excellence and our 13 traditional restaurant locations.',
  },
  nav: {
    brand: 'Doña Blanca',
    business: 'Business Solutions',
    operation: 'Our Operation',
    locations: 'Our Locations',
    invoice: 'Contact & Invoicing',
    cta: "Let's talk about your operation",
    switchLang: 'ES',
    switchLangUrl: '/',
  },
  home: {
    hero: {
      headline: 'Flavor, every day.',
      headlineAccent: 'Operations at scale.',
      body: 'From our own restaurant locations to foodservice solutions for businesses, we combine Mexican cuisine, operational experience and processes designed to deliver consistency every day.',
      ctaBusiness: 'Talk to us about your operation',
      ctaLocations: 'Explore our locations',
    },
    stats: {
      sinceLabel: 'Since',
      locationsLabel: 'Locations',
      pointsLabel: 'Operational Points',
      platesLabel: 'Plates served',
      tagline: 'We serve every day.',
      subtagline: "At our own locations and across our clients' operations.",
    },
    paths: {
      b2b: {
        eyebrow: 'FOR ENTERPRISES',
        title: 'Foodservice designed around your operation.',
        text: 'Turnkey foodservice solutions for organizations, from single corporate hubs to multi-site operations with centralized production, logistics and tracking.',
        cta: 'Discover our solutions →',
        href: '/en/business',
      },
      b2c: {
        eyebrow: 'OUR LOCATIONS',
        title: 'Doña Blanca every single day.',
        text: 'Breakfast, daily Mexican specials, artisan-baked breads and the warm neighborhood experience where our brand was born.',
        cta: 'Explore our locations →',
        href: '/en/locations',
      },
    },
  },
  business: {
    hero: {
      eyebrow: 'SOLUTIONS FOR BUSINESSES',
      title: 'Foodservice designed around',
      titleAccent: 'your operation.',
      body: 'We know every organization operates differently. That is why we engineer foodservice models customized to your headcount, shift schedules, physical infrastructure and culinary requirements—backed by the quality, consistency and expertise that define us.',
      cta: "Talk to us about your operation →",
    },
    solutions: [
      {
        num: '01',
        title: 'Industrial Dining Services',
        desc: 'Daily institutional foodservice for manufacturing facilities, logistics hubs and large workforces requiring wholesome, dependable nutrition at scale. From menu engineering to preparation and sanitary food dispatch.',
      },
      {
        num: '02',
        title: 'Corporate Dining',
        desc: 'High-standard dining experiences for headquarters and corporate offices focused on workplace satisfaction, wellness, and team productivity.',
      },
      {
        num: '03',
        title: 'Multi-site Operations',
        desc: 'Standardized food operations for organizations operating across multiple metropolitan facilities under one consolidated management team.',
      },
      {
        num: '04',
        title: 'Centralized Production & Distribution',
        desc: 'Central commissary preparation and cold-chain logistics to supply distributed dining points with reliable consistency.',
      },
      {
        num: '05',
        title: 'Tailored Solutions',
        desc: 'Every client operates under distinct operational realities. We custom-build service protocols around the specific needs of your enterprise.',
      },
    ],
    operation: {
      id: 'operacion',
      title: 'A lot happens before',
      titleAccent: 'a plate is served.',
      body: 'A truly reliable foodservice operation requires far more than just a kitchen. Procurement, production, specialized prep, order consolidation, refrigerated transit, and receiving form an unbroken chain. At Doña Blanca, we have developed our own infrastructure to oversee each stage and preserve unwavering quality as operations expand.',
      steps: [
        { num: '01', name: 'Procurement', desc: 'Sourcing premium, inspected agricultural and butcher inputs directly with trusted suppliers.' },
        { num: '02', name: 'Production', desc: 'Centralized food processing in dedicated facilities operating under strict food safety guidelines.' },
        { num: '03', name: 'Kitchen', desc: 'Authentic homestyle Mexican recipes cooked with calibrated repeatability.' },
        { num: '04', name: 'Bakery', desc: 'Freshly baking our signature pan dulce and artisan rolls daily in our internal bakery.' },
        { num: '05', name: 'Distribution', desc: 'Fleet logistics delivering just-in-time to all cafeterias and client facilities.' },
        { num: '06', name: 'Receiving', desc: 'Multi-point verification and temperature logs upon arrival at destination.' },
      ],
    },
    production: {
      title: 'Greater control begins right at preparation.',
      body: 'Owning our internal production facilities grants complete operational visibility over the most vital link of the foodservice supply chain. Our operations integrate specialized culinary units working in unison to supply daily demands.',
      highlights: [
        { title: 'CENTRAL COMMISSARY', desc: 'Standardized bases, stocks, signature salsas and master preparations with strict traceability.' },
        { title: 'IN-HOUSE BAKERY', desc: 'Daily baking of traditional Mexican sweet breads, bolillos, and fresh pastries.' },
        { title: 'SPECIALIZED PREP', desc: 'Dedicated clean-room prep lines for butchery, produce, dairy and cold pantry.' },
        { title: 'COORDINATED SUPPLY', desc: 'Seamless daily transfers from our central hub directly to each customer dining room.' },
      ],
      closing: 'This infrastructure enables a consistent, structured, and growth-ready operational backbone.',
    },
    bakery: {
      eyebrow: 'IN-HOUSE BAKERY',
      title: 'There are things we prefer',
      titleAccent: 'to keep baking ourselves.',
      body: 'We produce our own fresh breads and desserts as an essential pillar of our daily service. Having proprietary bakery facilities grants full control over recipe authenticity, daily freshness, texture and supply dependability.',
    },
    tech: {
      title: 'A connected operation',
      titleAccent: 'from start to finish.',
      body: 'As an operation scales, real-time insight into each operational phase becomes just as critical as great cooking. That is why we integrate dedicated digital tooling to track daily requisitions and maintain end-to-end oversight across every stage.',
      flow: [
        { name: 'Requisition', note: 'Demand planning' },
        { name: 'Production', note: 'Central batching' },
        { name: 'Preparation', note: 'Kitchen & bakery' },
        { name: 'Transit', note: 'Scheduled delivery' },
        { name: 'Receiving', note: 'On-site verification' },
      ],
      points: [
        'More traceability.',
        'More control.',
        'Better response capabilities.',
      ],
      clarification: 'Technology does not replace operational craftsmanship. It empowers it to remain transparent, coordinated, and scalable.',
    },
    flexibility: {
      title: 'Every operation has its own reality.',
      factors: ['Headcount', 'Shift Hours', 'Distances', 'Multiple Facilities', 'Variable Schedules', 'Workforce Profiles'],
      body: 'Our proposals do not come from a one-size-fits-all playbook. We examine how your business operates and engineer a dining solution that performs reliably 365 days a year.',
      cta: 'Tell us how your operation functions →',
    },
    differentiator: {
      title: 'We know how to cook.',
      titleAccent: 'We know how to operate.',
      body: 'Our distinct advantage lies in unifying capabilities that typically remain fragmented.',
      pillars: [
        { title: 'Proven Daily Operation', desc: 'We operate on the front lines every day with our own seasoned workforce.' },
        { title: 'In-House Food Production', desc: 'Central commissaries assuring flavor and consistency at every location.' },
        { title: 'Proprietary Artisan Bakery', desc: 'Fresh daily baked goods crafted as the cornerstone of our culinary identity.' },
        { title: 'Multi-Site Capability', desc: 'Standardized operational playbooks across all business campuses.' },
        { title: 'End-to-End Traceability', desc: 'Digital tracking tools enforcing sanitary compliance and operational transparency.' },
        { title: 'Close Customer Rapport', desc: 'Decades of direct consumer dining experience guiding every meal we serve.' },
      ],
    },
    contact: {
      id: 'contacto',
      eyebrow: 'COMMERCIAL INQUIRIES',
      title: "Let's talk about your operation.",
      body: 'Every company has a unique scenario. Tell us your workforce headcount, operational locations, and current dining setup. Our team will tailor a dining solution designed around your organization.',
      points: [
        { title: 'Tailored Account Guidance', desc: 'A dedicated operational specialist will partner with you throughout.' },
        { title: 'Custom Operational Design', desc: 'Menus and staffing schedules built around your exact shift patterns.' },
        { title: 'Comprehensive CDMX Reach', desc: 'Serving metropolitan industrial corridors and corporate parks with consolidated fleets.' },
      ],
      form: {
        empresa: 'Company',
        empresaPlaceholder: 'Your company name',
        nombre: 'Full Name',
        nombrePlaceholder: 'Your full name',
        correo: 'Corporate Email',
        correoPlaceholder: 'you@company.com',
        telefono: 'Phone Number',
        telefonoPlaceholder: '+52 55 1234 5678',
        ciudad: 'City',
        personas: 'Approximate Headcount',
        personasPlaceholder: 'e.g. 250',
        ubicaciones: 'Number of Locations',
        ubicacionesPlaceholder: 'e.g. 3',
        tipoServicio: 'Service Type',
        tipoServicioOptions: [
          'Industrial Dining Services',
          'Corporate Dining',
          'Multi-site Operations',
          'Production & Distribution',
          'Other',
        ],
        mensaje: 'Briefly describe your requirements',
        mensajePlaceholder: 'Tell us about shift hours, physical cafeteria facilities, or target launch date.',
        submitBtn: 'Connect with Doña Blanca',
        whatsappBtn: 'Contact via WhatsApp',
        disclaimer: 'Demonstration inquiry form — live CRM endpoint connection pending official rollout.',
        demoSuccess: 'Inquiry details saved locally for demonstration review. Our representative will follow up.',
      },
    },
  },
  b2c: {
    hero: {
      eyebrow: 'OUR LOCATIONS',
      title: 'Before we learned to operate at scale,',
      titleAccent: 'we learned to serve one person at a time.',
      body: 'Our locations are where the Doña Blanca story began. Every day we serve breakfast, lunch and freshly baked bread with the same warmth and consistency that have been part of our brand since the beginning.',
      cta: 'Find your nearest Doña Blanca →',
    },
    flavor: {
      eyebrow: 'OUR FLAVOR',
      title: 'Scale matters.',
      titleAccent: 'Flavor matters too.',
      body: 'At each of our locations we serve dishes crafted with honest ingredients, heritage Mexican home recipes, and warm bakery items fresh out of the oven all day long.',
      cards: [
        {
          title: 'Breakfast',
          desc: 'Morning staples made to order: authentic chilaquiles, huevos al gusto, café de olla and seasonal fruit.',
          tag: 'Mon–Fri 8 to 13 hrs | Sat–Sun 9 to 14 hrs',
        },
        {
          title: 'Mexican Lunch',
          desc: 'Three-course traditional daily meal with comforting stews, fresh tortillas and house dessert.',
          tag: 'Mon–Fri 13 to 17 hrs',
        },
        {
          title: 'Fresh Bread & Desserts',
          desc: 'Freshly baked conchas, croissants and artisan rolls crafted daily by our master bakers.',
          tag: 'Baked Fresh Every Morning',
        },
      ],
    },
    bakery: {
      eyebrow: 'IN-HOUSE BAKERY',
      title: 'Freshly baked bread every day.',
      body: 'Our bakery goods and desserts are an essential part of the daily Doña Blanca experience and are crafted inside our own dedicated bakeries.',
    },
    history: {
      eyebrow: 'OUR STORY',
      title: 'Cook with passion. Serve with pride. Every single day.',
      body: 'Doña Blanca was established in 2010 around a simple premise: create a warm, inviting dining table for people to enjoy comforting daily Mexican meals. Over time, our locations, teams, and logistics grew. Yet our purpose remains true to our roots: cook well, serve warmly, and do so every single day.',
    },
    directory: {
      id: 'directorio',
      eyebrow: 'LOCATION DIRECTORY',
      title: 'Find your nearest Doña Blanca in Mexico City',
      searchPlaceholder: 'Search by branch name or neighborhood (e.g. Del Valle, Condesa, Cuauhtémoc)...',
      allBadge: 'All 14 locations',
      howToGet: 'Directions',
      breakfastHours: 'Breakfast:',
      lunchHours: 'Lunch:',
      viewMenu: 'View Menu',
      orderDelivery: 'Order on Rappi',
      noResults: 'No locations found matching your search term.',
      missingDataNotice: 'Details updating',
    },
  },
  invoice: {
    eyebrow: 'CUSTOMER SERVICE',
    title: "We're here to help",
    subtitle: 'Whether you want to learn about our corporate dining solutions, have a question, or request an invoice, we are here to help.',
    contact: {
      title: "Let's talk",
      text: 'Have a question, feedback, or want to learn more about Doña Blanca? Write to us and our team will follow up on your message.',
      successMsg: 'Thank you for writing to us! We have received your message and our team will follow up.',
      btn: 'Send message',
      sendingBtn: 'Sending message...',
      reasons: [
        'General Information',
        'Corporate Dining Solutions',
        'Our Locations',
        'Feedback or Suggestions',
        'Other',
      ],
    },
    billing: {
      title: 'Request your invoice',
      text: 'Share your tax information and receipt details. Our administrative team will review your request and send the official invoice to the email provided.',
      notice: 'Invoices are not generated automatically. Our team will review your request and complete the official process.',
      successMsg: 'Request received! Our administrative team will review your information and process your invoice.',
      btn: 'Submit invoice request',
      sendingBtn: 'Submitting request...',
      regimes: [
        '601 - General Corporate Law Entities',
        '603 - Non-Profit Corporate Entities',
        '605 - Salaries and Wages',
        '606 - Leasing and Rentals',
        '612 - Individuals with Business & Professional Activities',
        '621 - Fiscal Incorporation (RIF)',
        '626 - Simplified Trust Regime (RESICO)',
        'Other Tax Regime',
      ],
      cfdiUses: [
        'G03 - General Expenses',
        'G01 - Acquisition of Goods',
        'S01 - No Fiscal Effect',
        'CP01 - Payments',
        'Other CFDI Purpose',
      ],
    },
  },
  footer: {
    tagline: 'Flavor, every day. Operations at scale.',
    links: {
      business: 'Business Solutions',
      operation: 'Our Operation',
      locations: 'Our Locations',
      invoice: 'Contact',
      contact: 'Contact Us',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
    city: 'Mexico City, Mexico',
    quickLinksTitle: 'Quick Links',
    findBranch: 'Find a Location',
    findBranchDesc: 'Discover our 14 CDMX branches',
    seeMenu: 'Daily Menu',
    seeMenuDesc: 'Explore our traditional Mexican recipes',
    invoiceShort: 'Invoice Receipt',
    invoiceShortDesc: 'Fast CFDI billing generator',
    copyright: '© 2026 Doña Blanca. All rights reserved. "Doña Blanca" is a registered trademark of A+A Servicios.',
  },
};
